import React, { useState, useEffect } from 'react'
import * as JsSearch from 'js-search'
import { useQueryParam, StringParam } from 'use-query-params'
import { Link, useStaticQuery, graphql } from 'gatsby'
import styled from 'styled-components'
import { AiOutlineSearch, AiOutlineLoading3Quarters } from 'react-icons/ai'
import { debounce } from 'lodash'
import { device } from './Breakpoints'

const Container = styled.section`
  width: 100%;
  .loading {
    margin: auto;
    display: block;
    -webkit-animation: spin 2s linear infinite;
    -moz-animation: spin 2s linear infinite;
    animation: spin 2s linear infinite;
  }
  @-moz-keyframes spin {
    100% {
      -moz-transform: rotate(360deg);
    }
  }
  @-webkit-keyframes spin {
    100% {
      -webkit-transform: rotate(360deg);
    }
  }
  @keyframes spin {
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  .mobile {
    position: absolute !important;
    z-index: 9999 !important;
    background: #ffff;
    width: 100%;
    top: 81px;

    .search form {
      margin: 0;
    }
  }
  .search {
    h1 {
      margin-bottom: 0;
      color: #8e8fc7;
      font-weight: normal;
      font-size: 1.8em !important;
      strong {
        color: #ff8f8c;
        font-family: 'Santral Bold', Arial, Helvetica, sans-serif;
      }
    }
    h1.textCenter {
      text-align: center;
    }
    form {
      padding: 1em;
      background: #ff8f8c;
      margin-bottom: 2em;
      @media ${device.mobileL} {
        padding: 0.5em 1em;
        margin-bottom: 1em;
      }
      div {
        position: relative;
        box-sizing: border-box;
        svg {
          position: absolute;
          color: #ffffff;
          top: 6px;
          left: 10px;
          font-size: 1.7em;
          @media ${device.mobileL} {
            top: 2px;
            left: 6px;
          }
        }
      }
      input {
        border-radius: 50px;
        color: #ffffff;
        width: 100%;
        padding: 1em 1em 1em 3em;
        border: 1px solid #ffffff;
        background: transparent;
        box-sizing: border-box;
        ::placeholder {
          color: #ffffff;
        }
        @media ${device.mobileL} {
          padding: 0.7em 2.5em;
        }
      }
    }
    .inner {
      position: relative;
      h2 {
        font-size: 1.2em !important;
        font-family: 'Santral Bold', Arial, Helvetica, sans-serif;
        color: #148581;
      }
      ul {
        margin: 0 0 2em 0;
        padding: 0;
        li {
          border-bottom: 2px solid #8e8fc7;
          padding: 2em 0;
          list-style: none;
          :last-child {
            border-bottom: none;
          }
          a {
            color: #148581;
            text-decoration: none;
          }
          p {
            font-size: 0.8em;
          }
        }
      }
      .content {
        font-size: 0.8em;
        width: 50%;
        line-height: 1.6em;
        @media ${device.mobileL} {
          width: 100%;
          font-size: 1.5em;
          text-align: left;
        }
      }
      ${device.tablet} {
        width: 50%;
        font-weight: normal;
        font-size: 1em;
        margin-left: 5%;
      }
      @media ${device.mobileL} {
        width: 100%;
        font-weight: normal;
        font-size: 0.8em;
        margin-left: 0;
        box-sizing: border-box;
        margin: 0;
        padding: 1em 1.5em;
        h2 {
          position: initial;
          text-align: left;
          padding: 0;
        }
      }
    }
  }
`
const AlignCenter = styled.div`
  max-width: 960px;
  margin: auto;
  @media ${device.mobileL} {
    max-width: 100%;
  }
`

const SearchComponent = ({ mobile }) => {
  const data = useStaticQuery(graphql`
    query searchQuery {
      allWpPage(limit: 100) {
        nodes {
          id
          title
          uri
          excerpt
          content
          nodeType
        }
      }
      allWpPost(limit: 2000) {
        nodes {
          id
          title
          uri
          excerpt
          content
          nodeType
        }
      }
      allWpSemana(limit: 40) {
        nodes {
          id
          title
          uri
          content
          nodeType
        }
      }
    }
  `)

  const [pageObj, setPages] = useState([])
  const [searchObj, setSearch] = useState([])
  const [searchResultsObj, setSearchResults] = useState([])
  const [loadingObj, setLoading] = useState(true)
  const [initialQuery, setInitialQuery] = useQueryParam('s', StringParam)
  const [queryObj, setQuery] = useState('')

  const rebuildIndex = () => {
    const dataToSearch = new JsSearch.Search('title')

    dataToSearch.indexStrategy = new JsSearch.PrefixIndexStrategy()
    dataToSearch.sanitizer = new JsSearch.LowerCaseSanitizer()
    dataToSearch.searchIndex = new JsSearch.TfIdfSearchIndex('title')
    dataToSearch.addIndex('id') // sets the index attribute for the data
    dataToSearch.addIndex('title') // sets the index attribute for the data
    dataToSearch.addIndex('author') // sets the index attribute for the data
    dataToSearch.addIndex('content') // sets the index attribute for the data
    dataToSearch.addIndex('excerpt') // sets the index attribute for the data
    dataToSearch.addIndex('uri') // sets the index attribute for the data
    dataToSearch.addDocuments(pageObj) // adds the data to be searched
    setSearch(dataToSearch)
    setLoading(false)
  }
  const debounceSearch = debounce(query => {
    rebuildIndex()
    const sanitizedQuery = query
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(' ', '-')
    const queryResult = searchObj.search(sanitizedQuery)
    setQuery(query)
    setSearchResults(queryResult)
  }, 2000)

  const searchData = e => {
    const target = e.target.value
    setLoading(true)
    debounceSearch(target)
  }

  const handleSubmit = e => {
    e.preventDefault()
  }

  useEffect(() => {
    ;(() => {
      const pages = data.allWpPage.nodes
      const posts = data.allWpPost.nodes
      const babypedia = data.allWpSemana.nodes
      const pageData = [].concat(pages, posts, babypedia)

      setPages(pageData)
      rebuildIndex()

      if (initialQuery) {
        const dataToSearch = new JsSearch.Search('title')

        dataToSearch.indexStrategy = new JsSearch.PrefixIndexStrategy()
        dataToSearch.sanitizer = new JsSearch.LowerCaseSanitizer()
        dataToSearch.searchIndex = new JsSearch.TfIdfSearchIndex('title')
        dataToSearch.addIndex('id') // sets the index attribute for the data
        dataToSearch.addIndex('title') // sets the index attribute for the data
        dataToSearch.addIndex('excerpt') // sets the index attribute for the data
        dataToSearch.addIndex('content') // sets the index attribute for the data
        dataToSearch.addIndex('uri') // sets the index attribute for the data
        dataToSearch.addDocuments(pageData) // adds the data to be searched
        const queryResult = dataToSearch.search(initialQuery)
        setQuery(initialQuery)
        setSearchResults(queryResult)
      }
    })()
  }, [])

  const queryResults = queryObj === '' ? '' : searchResultsObj

  return (
    <Container>
      <div className={mobile ? 'mobile' : null}>
        <section className="search">
          <form onSubmit={handleSubmit}>
            <AlignCenter>
              <AiOutlineSearch />
              <input
                id="Search"
                onChange={searchData}
                placeholder="Insira sua busca"
              />
            </AlignCenter>
          </form>
          <AlignCenter>
            {loadingObj ? (
              <AiOutlineLoading3Quarters className="loading" />
            ) : (
              ''
            )}
            {queryResults.length > 0 ? (
              <div className="inner">
                <h1>
                  Foram encontrados{' '}
                  <strong>{queryResults.length} resultado(s)</strong> para{' '}
                  <strong>{queryObj === '' ? 'todo site' : queryObj}</strong>
                </h1>
                <ul>
                  {queryResults.map(item => {
                    return (
                      <li key={item.id}>
                        {item.nodeType === 'Post' ? (
                          <h2>
                            <Link to={`/blog${item.uri}`}>{item.title}</Link>
                          </h2>
                        ) : (
                          <h2>
                            <Link to={`${item.uri}`}>{item.title}</Link>
                          </h2>
                        )}

                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              item.nodeType !== 'Semana'
                                ? item.excerpt
                                : item.content,
                          }}
                        />
                        {item.nodeType === 'Post' ? (
                          <Link to={`/blog${item.uri}`}>
                            https://www.santajoana.com.br/blog{item.uri}
                          </Link>
                        ) : (
                          <Link to={`${item.uri}`}>
                            https://www.santajoana.com.br{item.uri}
                          </Link>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : (
              <>
                <h1 className="textCenter">Nenhum resultado encontrado!</h1>
                <br />
              </>
            )}
          </AlignCenter>
        </section>
      </div>
    </Container>
  )
}

export default SearchComponent
